/**
 * BugReportManager - Handles bug report collection and submission
 *
 * Collects:
 * - User description
 * - Console/debug log history
 * - Current save state
 * - Device/browser info
 *
 * Submits to:
 * - Firebase Firestore (permanent storage)
 * - Discord webhook (instant notification)
 */

import { db, isConfigured } from '../firebase/config.js';
import { collection, addDoc, serverTimestamp } from 'https://www.gstatic.com/firebasejs/10.7.1/firebase-firestore.js';

const DISCORD_WEBHOOK_URL = 'https://discord.com/api/webhooks/1456016398475071498/1WlMChVKz43ZkFCg3D2hEk1WxNvsgRoZBA8EB0MjDD29DWLtomM9IP6NtL9F-wyLwuh-';

export class BugReportManager {
    constructor(gameState, accountManager = null) {
        this.gameState = gameState;
        this.accountManager = accountManager;
    }

    /**
     * Collect all debug/diagnostic data
     */
    collectDiagnostics() {
        const diagnostics = {
            // Device and browser info
            device: {
                userAgent: navigator.userAgent,
                platform: navigator.platform,
                language: navigator.language,
                screenWidth: window.screen.width,
                screenHeight: window.screen.height,
                viewportWidth: window.innerWidth,
                viewportHeight: window.innerHeight,
                devicePixelRatio: window.devicePixelRatio,
                isMobile: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
                online: navigator.onLine
            },

            // Timestamp
            timestamp: new Date().toISOString(),

            // User info
            user: {
                isAnonymous: this.accountManager?.currentUser?.isAnonymous ?? true,
                userId: this.accountManager?.currentUser?.uid || 'anonymous',
                displayName: this.accountManager?.currentUser?.displayName || null
            },

            // Current save slot
            saveSlot: this.gameState?.saveManager?.getActiveSaveKey?.() || 'unknown'
        };

        return diagnostics;
    }

    /**
     * Get debug log history from localStorage or app instance
     */
    getDebugLogs() {
        try {
            // Try to get from localStorage first
            const stored = localStorage.getItem('roadtripDebugLog');
            if (stored) {
                return JSON.parse(stored);
            }
        } catch (e) {
            console.warn('[BugReport] Failed to get debug logs:', e);
        }
        return [];
    }

    /**
     * Get current save state (sanitized)
     */
    getSaveState() {
        try {
            const state = this.gameState?.state;
            if (!state) return null;

            // Return a copy to avoid mutation
            return JSON.parse(JSON.stringify(state));
        } catch (e) {
            console.warn('[BugReport] Failed to get save state:', e);
            return null;
        }
    }

    /**
     * Get recent console errors (if captured)
     */
    getConsoleErrors() {
        // If we've set up console error capture, return them
        return window._capturedConsoleErrors || [];
    }

    /**
     * Submit bug report
     * @param {string} description - User's description of the bug
     * @returns {Promise<{success: boolean, reportId?: string, error?: string}>}
     */
    async submit(description) {
        if (!description || description.trim().length === 0) {
            return { success: false, error: 'Description is required' };
        }

        const report = {
            description: description.trim(),
            diagnostics: this.collectDiagnostics(),
            debugLogs: this.getDebugLogs(),
            saveState: this.getSaveState(),
            consoleErrors: this.getConsoleErrors(),
            submittedAt: new Date().toISOString()
        };

        let reportId = null;

        // 1. Save to Firestore (if configured)
        if (isConfigured && db) {
            try {
                const docRef = await addDoc(collection(db, 'bugReports'), {
                    ...report,
                    _serverTimestamp: serverTimestamp()
                });
                reportId = docRef.id;
                console.log('[BugReport] Saved to Firestore:', reportId);
            } catch (err) {
                console.error('[BugReport] Failed to save to Firestore:', err);
                // Continue to Discord even if Firestore fails
            }
        }

        // 2. Send Discord notification (best-effort, may fail due to CORS)
        // Note: Discord webhooks don't support CORS from browsers
        // For reliable Discord notifications, use a Cloud Function trigger on bugReports collection
        try {
            await this.sendDiscordNotification(report, reportId);
            console.log('[BugReport] Discord notification attempted (CORS may block)');
        } catch (err) {
            // Expected to fail from browser due to CORS - Firestore is the primary storage
            console.log('[BugReport] Discord skipped (CORS restriction) - report saved to Firestore');
        }

        return {
            success: true,
            reportId: reportId || 'discord-only'
        };
    }

    /**
     * Send notification to Discord webhook
     */
    async sendDiscordNotification(report, reportId) {
        const { diagnostics, description } = report;

        // Truncate description for Discord embed
        const shortDesc = description.length > 500
            ? description.substring(0, 497) + '...'
            : description;

        // Build embed
        const embed = {
            title: 'New Bug Report',
            color: 0xFF6B6B, // Red-ish color
            fields: [
                {
                    name: 'Description',
                    value: shortDesc || 'No description provided',
                    inline: false
                },
                {
                    name: 'User',
                    value: diagnostics.user.isAnonymous
                        ? `Anonymous`
                        : `${diagnostics.user.displayName || 'Unknown'} (${diagnostics.user.userId})`,
                    inline: true
                },
                {
                    name: 'Device',
                    value: diagnostics.device.isMobile ? 'Mobile' : 'Desktop',
                    inline: true
                },
                {
                    name: 'Save Slot',
                    value: diagnostics.saveSlot,
                    inline: true
                },
                {
                    name: 'Screen',
                    value: `${diagnostics.device.viewportWidth}x${diagnostics.device.viewportHeight}`,
                    inline: true
                },
                {
                    name: 'Debug Logs',
                    value: `${report.debugLogs?.length || 0} entries`,
                    inline: true
                }
            ],
            footer: {
                text: reportId
                    ? `Report ID: ${reportId}`
                    : 'Not saved to Firestore'
            },
            timestamp: new Date().toISOString()
        };

        // Add browser info as a separate field
        // Check for mobile browser variants first (CriOS = Chrome iOS, FxiOS = Firefox iOS)
        const ua = diagnostics.device.userAgent;
        let browserInfo = null;

        // Check iOS browser variants first (they also contain "Safari" in UA)
        const criosMatch = ua.match(/CriOS\/([\d.]+)/);
        const fxiosMatch = ua.match(/FxiOS\/([\d.]+)/);
        const edgiosMatch = ua.match(/EdgiOS\/([\d.]+)/);

        if (criosMatch) {
            browserInfo = `Chrome iOS/${criosMatch[1]}`;
        } else if (fxiosMatch) {
            browserInfo = `Firefox iOS/${fxiosMatch[1]}`;
        } else if (edgiosMatch) {
            browserInfo = `Edge iOS/${edgiosMatch[1]}`;
        } else {
            // Standard desktop/Android browsers
            const browserMatch = ua.match(/(Chrome|Firefox|Safari|Edge|Opera)\/[\d.]+/);
            if (browserMatch) {
                browserInfo = browserMatch[0];
            }
        }

        if (browserInfo) {
            embed.fields.push({
                name: 'Browser',
                value: browserInfo,
                inline: true
            });
        }

        const payload = {
            username: 'Bug Bot',
            avatar_url: 'https://em-content.zobj.net/source/apple/391/lady-beetle_1f41e.png',
            embeds: [embed]
        };

        // Use no-cors mode since Discord webhooks don't support CORS from browsers
        // We won't get a response, but the request will be sent
        await fetch(DISCORD_WEBHOOK_URL, {
            method: 'POST',
            mode: 'no-cors',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
        });
        // Note: no-cors mode always returns opaque response, can't check status
    }
}

/**
 * Set up console output capturing (call early in app initialization)
 * Captures console.log, console.warn, console.error, and unhandled errors
 */
export function setupConsoleErrorCapture() {
    window._capturedConsoleErrors = [];
    const maxEntries = 200;

    // Helper to format console arguments
    const formatArgs = (args) => {
        return args.map(arg => {
            if (arg instanceof Error) {
                return `${arg.message}\n${arg.stack}`;
            }
            if (typeof arg === 'object') {
                try {
                    return JSON.stringify(arg, null, 0);
                } catch (e) {
                    return String(arg);
                }
            }
            return String(arg);
        }).join(' ');
    };

    // Helper to add entry with max limit
    const addEntry = (entry) => {
        window._capturedConsoleErrors.push(entry);
        if (window._capturedConsoleErrors.length > maxEntries) {
            window._capturedConsoleErrors.shift();
        }
    };

    // Capture console.log
    const originalLog = console.log;
    console.log = function(...args) {
        addEntry({
            timestamp: new Date().toISOString(),
            level: 'log',
            message: formatArgs(args)
        });
        originalLog.apply(console, args);
    };

    // Capture console.warn
    const originalWarn = console.warn;
    console.warn = function(...args) {
        addEntry({
            timestamp: new Date().toISOString(),
            level: 'warn',
            message: formatArgs(args)
        });
        originalWarn.apply(console, args);
    };

    // Capture console.error
    const originalError = console.error;
    console.error = function(...args) {
        addEntry({
            timestamp: new Date().toISOString(),
            level: 'error',
            message: formatArgs(args)
        });
        originalError.apply(console, args);
    };

    // Also capture unhandled errors
    window.addEventListener('error', (event) => {
        addEntry({
            timestamp: new Date().toISOString(),
            level: 'error',
            message: `Unhandled: ${event.message} at ${event.filename}:${event.lineno}`,
            stack: event.error?.stack
        });
    });

    // Capture unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
        addEntry({
            timestamp: new Date().toISOString(),
            level: 'error',
            message: `Unhandled Promise: ${event.reason}`,
            stack: event.reason?.stack
        });
    });
}
